import { Route, Routes, useNavigate } from 'react-router-dom'
import NotFound from 'routes/NotFound'
import Page from 'components/global/Page'
import { useApp } from 'store/steps'
import { setCart } from 'store/cart-slice'
import { useCreateCartMutation, useGetCartQuery } from 'store/api-slice/hooks'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { useEffect } from 'react'
import FullPageLoader from 'components/global/FullPageLoader'
import ServerErrorModal from 'components/global/ServerErrorModal'
import { useUnlimited } from 'store/unlimited-slice/hooks'
import { setIsAppAccessible, setIsErrorModalOpened } from 'store/general-slice'
import CardConfiguration from 'routes/unlimited-card/CardConfiguration'
import CardSummary from 'routes/unlimited-card/CardSummary'
import CardOrder from 'routes/unlimited-card/CardOrder'
import steps from 'index'
import UnlimitedCalendarModal from 'components/global/CalendarModal/forks/UnlimitedCalendarModal'
import { pushDataLayer } from 'helpers/analytics'
import {
    restoreCardsfromCart,
    setIsCalendarModalOpened,
} from 'store/unlimited-slice'
import { CART_TYPE_CARD } from 'constants/app'
import {
    unlimitedCardFirstStep,
    unlimitedCardSummaryStep,
} from 'routes/basenames'
import { catchErrorByStatus } from 'helpers/errors'
import ConfirmationModal from 'components/global/ConfirmationModal'
import { STATUS_CODE_NO_SESSION } from 'constants/system'
import { DEFAULT_LANG } from 'constants/i18n'

const UnlimitedCard = () => {
    const navigateTo = useNavigate()
    const dispatch = useAppDispatch()
    const { getPath, language, stepIndex, appMode } = useApp()
    const { removeCardsFromCart, isProcessing } = useUnlimited()

    const { cart } = useAppSelector((state) => state.cart)
    const { isCalendarModalOpened } = useAppSelector((state) => state.unlimited)

    const {
        isFetching: isCartFetching,
        isError: isCartError,
        data: cartData,
        error: cartError,
    } = useGetCartQuery({
        _locale: language ?? DEFAULT_LANG,
        cartCreatorType: CART_TYPE_CARD,
    })
    const [createCart, createCartStatus] = useCreateCartMutation()

    const {
        isError: isCreateCartError,
        isSuccess: isCreateCartSuccess,
        data: createCartData,
    } = createCartStatus
    const isCartAlreadyCreated = cart?.cartId
    const isVerifiedUrl = stepIndex !== -1
    const isFirstStep =
        isVerifiedUrl && steps[stepIndex]?.id === 'card_configuration'

    const handleAcceptError = () => {
        removeCardsFromCart(true).then(() => {
            dispatch(setIsErrorModalOpened(false))
            window.location.replace(unlimitedCardFirstStep)
        })
    }

    const handleCloseCalendar = () => {
        dispatch(setIsCalendarModalOpened(false))
    }

    useEffect(() => {
        if (isCartFetching || isCartAlreadyCreated) return

        if (isCartError && cartError) {
            if (
                catchErrorByStatus({
                    error: cartError,
                    ignored: [STATUS_CODE_NO_SESSION],
                })
            )
                return

            if (!isFirstStep && isVerifiedUrl) {
                window.location.replace(unlimitedCardFirstStep)
                return
            }

            createCart({
                _locale: language ?? DEFAULT_LANG,
                cartCreatorType: CART_TYPE_CARD,
            })
            return
        }

        if (cartData) {
            const cartItems = cartData.cart?.items

            if (
                cartItems &&
                cartItems.length === 0 &&
                !isFirstStep &&
                isVerifiedUrl
            ) {
                navigateTo('/')
                return
            }

            dispatch(setCart({ ...cartData, appMode }))

            if (cartItems && cartItems.length > 0) {
                dispatch(
                    restoreCardsfromCart(
                        cartItems.filter(
                            (item) =>
                                item.productType === 'card' ||
                                item.productType === 'bundle'
                        )
                    )
                )

                if (isFirstStep && isVerifiedUrl)
                    window.location.replace(unlimitedCardSummaryStep)
            }
        }
    }, [isCartFetching])

    useEffect(() => {
        isCreateCartError &&
            dispatch(setIsAppAccessible({ isAppAccessible: false }))
    }, [isCreateCartError])

    useEffect(() => {
        if (!isCreateCartSuccess) return

        dispatch(
            setCart({
                cart: { cartId: createCartData?.cartId },
                appMode,
                isFetchingLocked: true,
            })
        )
    }, [isCreateCartSuccess])

    useEffect(() => {
        // < analytics
        const storageRedirectSource = sessionStorage.getItem('unlimitedSource')
        let redirectSource = 'direct'

        if (storageRedirectSource) {
            redirectSource = storageRedirectSource
            sessionStorage.removeItem('unlimitedSource')
        }

        pushDataLayer({
            event: 'funnelStep',
            event_category: 'unlimited',
            event_action: 'unlimited_order_start',
            event_label: redirectSource,
        })
        // analytics >
    }, [])

    return !cart?.cartId ? (
        <FullPageLoader />
    ) : (
        <Page>
            <Routes>
                <Route path="/" element={<CardConfiguration />} />
                <Route
                    path={getPath('card_summary')}
                    element={<CardSummary />}
                />
                <Route path={getPath('card_order')} element={<CardOrder />} />
                <Route path="*" element={<NotFound />} />
            </Routes>
            <UnlimitedCalendarModal
                isOpened={isCalendarModalOpened}
                onClose={handleCloseCalendar}
            />
            <ServerErrorModal
                onClick={handleAcceptError}
                isLoading={isProcessing}
            />
            <ConfirmationModal />
        </Page>
    )
}

export default UnlimitedCard
